import React from 'react'
import { Alert, Button, Stack, CircularProgress } from '@mui/material'
import { useState, useEffect, useRef } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { TitledPaper } from '@dai/components'
import { ActionCodeInfo } from 'firebase/auth'
import SendVerifyAndChangeEmail from './SendVerifyAndChangeEmail'

interface HandleVerifyAndChangeEmailProps {
  oobCode: string
  signInUrl: string
  verifyAndUpdateEmail: (oobCode: string) => Promise<void>
  checkActionCode: (code: string) => Promise<ActionCodeInfo>
  sendVerifyBeforeUpdateEmail: (
    newEmail: string,
    continueUrl?: string | undefined,
  ) => Promise<void>
  signOut: () => Promise<void>
}

const HandleVerifyAndChangeEmail: React.FC<HandleVerifyAndChangeEmailProps> = ({
  oobCode,
  signInUrl,
  verifyAndUpdateEmail,
  checkActionCode,
  sendVerifyBeforeUpdateEmail,
  signOut,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [success, setSuccess] = useState<boolean>(false)
  const [email, setEmail] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const verificationAttempted = useRef<boolean>(false)

  useEffect(() => {
    const verifyEmailAddress = async () => {
      if (verificationAttempted.current) {
        return
      }

      verificationAttempted.current = true

      try {
        const actionCodeInfo = await checkActionCode(oobCode)
        setEmail(actionCodeInfo.data.email || null)
        await verifyAndUpdateEmail(oobCode)
        setSuccess(true)
        setLoading(false)
      } catch (error: any) {
        setSuccess(false)
        setLoading(false)
      }
    }

    verifyEmailAddress()
  }, [oobCode, verifyAndUpdateEmail, checkActionCode])

  if (loading) {
    return (
      <TitledPaper
        title="Update Email"
        subtitle="Verifying your email address..."
      >
        <CircularProgress />
      </TitledPaper>
    )
  }

  return (
    <TitledPaper
      title="Update Email"
      subtitle={
        success
          ? 'Your email has been verified and updated'
          : 'Try updating your email again'
      }
    >
      <Stack spacing={4} width="100%">
        {success ? (
          <>
            <Alert severity="success">
              Your email has been successfully updated. You can now sign in with
              your new email <strong>{email}</strong>.
            </Alert>
            <Button
              fullWidth
              color="primary"
              variant="contained"
              component={RouterLink}
              to={signInUrl}
            >
              Sign In
            </Button>
          </>
        ) : (
          <SendVerifyAndChangeEmail
            initialError={
              'Your request to verify and update your email has expired or the link has already been used. Please try again.'
            }
            onCancel={() => {
              navigate(location.state?.from || '/', { replace: true })
            }}
            signInUrl={signInUrl}
            signOut={signOut}
            sendVerifyBeforeUpdateEmail={sendVerifyBeforeUpdateEmail}
          />
        )}
      </Stack>
    </TitledPaper>
  )
}

export default HandleVerifyAndChangeEmail
